import {
  type AnalyticsChatBreakdownItem,
  type AnalyticsOverviewResponse,
  type AnalyticsTimelinePoint,
  type AuditLogListItem,
  type AuthMeResponse,
  type ChatGreetingSettingsResponse,
  type ChatListItem,
  type ChatMemberDetail,
  type ChatMemberExportResponse,
  type ChatMemberListItem,
  type ConfirmationRequestResponse,
  type DashboardAlert,
  type DashboardPeriod,
  type DashboardStatsResponse,
  type DeleteRemindersByChatResponse,
  type DeliveryListItem,
  type DeliveryMessageResponse,
  type HealthResponse,
  type MessageTemplateResponse,
  type PaginatedResponse,
  type ReminderDryRunResponse,
  type ReminderMediaResponse,
  type ReminderNextRunExplanation,
  type ReminderQueueStateResponse,
  type ReminderRecalculateResponse,
  type ReminderRescheduleDebugResponse,
  type ReminderResponse,
  type ReminderTimelineResponse,
  type SendReminderNowResponse,
  type TelegramApiAvailabilityResponse,
  type TelegramApiIntegrationSaveResponse,
  type TelegramApiIntegrationStatusResponse,
  type TelegramUserProfileResponse,
} from '@irl/types';

/** Relative URL uses Vite dev proxy to API in local development. */
const API_URL =
  typeof import.meta.env.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL.length > 0
    ? import.meta.env.VITE_API_URL
    : '/api/v1';

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  if (token) {
    localStorage.setItem('irl_token', token);
  } else {
    localStorage.removeItem('irl_token');
  }
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  return localStorage.getItem('irl_token');
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (options.body != null) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  }).catch((err: unknown) => {
    if (err instanceof TypeError) {
      throw err;
    }
    throw new TypeError('Failed to fetch');
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: { message?: string; code?: string };
    };
    const message = error.error?.message ?? `Request failed: ${String(response.status)}`;
    const code = error.error?.code ?? 'REQUEST_FAILED';
    throw new ApiError(message, code, response.status);
  }

  return response.json() as Promise<T>;
}

async function requestPhoto(path: string): Promise<Blob> {
  const token = getAuthToken();
  const response = await fetch(`${API_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new ApiError('Photo unavailable', 'REQUEST_FAILED', response.status);
  return response.blob();
}

export const api = {
  health: {
    get: () => request<HealthResponse>('/health'),
  },
  auth: {
    login: (initData: string) =>
      request<{ token: string; expiresAt: string }>('/auth/telegram', {
        method: 'POST',
        body: JSON.stringify({ initData }),
      }),
    me: () => request<AuthMeResponse>('/auth/me'),
    profile: () => request<TelegramUserProfileResponse>('/auth/me/profile'),
    photo: () => requestPhoto('/auth/me/photo'),
  },
  reminders: {
    confirmations: (id: string) =>
      request<ConfirmationRequestResponse[]>(`/reminders/${id}/confirmations`),
    previewName: (params: { chatId: string; timezone: string; scheduledAt: string }) =>
      request<{ name: string }>(`/reminders/preview-name?${new URLSearchParams(params)}`),
    list: (params?: Record<string, string>) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return request<PaginatedResponse<ReminderResponse>>(`/reminders${query}`);
    },
    get: (id: string) => request<ReminderResponse>(`/reminders/${id}`),
    create: (data: unknown) =>
      request<ReminderResponse>('/reminders', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: unknown) =>
      request<ReminderResponse>(`/reminders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) => request<{ success: boolean }>(`/reminders/${id}`, { method: 'DELETE' }),
    deleteByChat: (chatId: string) =>
      request<DeleteRemindersByChatResponse>(`/chats/${chatId}/reminders`, { method: 'DELETE' }),
    previewSchedule: (data: unknown) =>
      request<{ nextRuns: string[] }>('/reminders/preview-schedule', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    sendNow: (id: string) =>
      request<SendReminderNowResponse>(`/reminders/${id}/send-now`, { method: 'POST' }),
    reschedule: (id: string) =>
      request<ReminderResponse>(`/reminders/${id}/reschedule`, { method: 'POST' }),
    explainNextRun: (id: string) =>
      request<ReminderNextRunExplanation>(`/reminders/${id}/explain-next-run`),
    getQueueState: (id: string) =>
      request<ReminderQueueStateResponse>(`/reminders/${id}/queue-state`),
    getTimeline: (id: string) => request<ReminderTimelineResponse>(`/reminders/${id}/timeline`),
    recalculate: (id: string) =>
      request<ReminderRecalculateResponse>(`/reminders/${id}/recalculate`, { method: 'POST' }),
    dryRun: (id: string) =>
      request<ReminderDryRunResponse>(`/reminders/${id}/dry-run`, { method: 'POST' }),
    rescheduleDebug: (id: string) =>
      request<ReminderRescheduleDebugResponse>(`/reminders/${id}/reschedule`, { method: 'POST' }),
    uploadMedia: async (file: File): Promise<ReminderMediaResponse> => {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${API_URL}/reminders/media`, {
        method: 'POST',
        headers,
        body,
      }).catch((err: unknown) => {
        if (err instanceof TypeError) {
          throw err;
        }
        throw new TypeError('Failed to fetch');
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => ({}))) as {
          error?: { message?: string; code?: string };
        };
        throw new ApiError(
          error.error?.message ?? 'Failed to upload photo',
          error.error?.code ?? 'REQUEST_FAILED',
          response.status,
        );
      }
      return response.json() as Promise<ReminderMediaResponse>;
    },
    media: async (id: string): Promise<Blob> => {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/reminders/media/${id}`, { headers });
      if (!response.ok) {
        throw new ApiError('Failed to load photo', 'NOT_FOUND', response.status);
      }
      return response.blob();
    },
  },
  chats: {
    list: () => request<ChatListItem[]>('/chats'),
    admins: (chatId: string) => request<ChatMemberListItem[]>(`/chats/${chatId}/admins`),
    greetings: {
      get: (chatId: string) => request<ChatGreetingSettingsResponse>(`/chats/${chatId}/greetings`),
      update: (chatId: string, data: ChatGreetingSettingsResponse) =>
        request<ChatGreetingSettingsResponse>(`/chats/${chatId}/greetings`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),
    },
    members: {
      profile: (chatId: string, userId: string) =>
        request<TelegramUserProfileResponse>(`/chats/${chatId}/members/${userId}/profile`),
      photo: (chatId: string, userId: string) =>
        requestPhoto(`/chats/${chatId}/members/${userId}/photo`),
      list: (chatId: string) => request<ChatMemberDetail[]>(`/chats/${chatId}/members`),
      export: (chatId: string) =>
        request<ChatMemberExportResponse>(`/chats/${chatId}/members/export`, {
          method: 'POST',
        }),
      updateRole: (chatId: string, userId: string, role: string) =>
        request<ChatMemberDetail>(`/chats/${chatId}/members/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify({ role }),
        }),
    },
    photo: async (chatId: string): Promise<Blob> => {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/chats/${chatId}/photo`, { headers });
      if (!response.ok) {
        const code =
          response.status === 401
            ? 'UNAUTHORIZED'
            : response.status === 403
              ? 'FORBIDDEN'
              : response.status === 429
                ? 'RATE_LIMITED'
                : 'NOT_FOUND';
        throw new ApiError('Failed to load chat photo', code, response.status);
      }
      return response.blob();
    },
  },
  audit: {
    list: (params?: Record<string, string>) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return request<PaginatedResponse<AuditLogListItem>>(`/audit-logs${query}`);
    },
  },
  dashboard: {
    stats: (period: DashboardPeriod = 'today') =>
      request<DashboardStatsResponse>(`/dashboard/stats?period=${period}`),
    alerts: (period: DashboardPeriod = 'today') =>
      request<DashboardAlert[]>(`/dashboard/alerts?period=${period}`),
  },
  analytics: {
    overview: (period: DashboardPeriod = '7d') =>
      request<AnalyticsOverviewResponse>(`/analytics/overview?period=${period}`),
    timeline: (period: DashboardPeriod = '7d') =>
      request<AnalyticsTimelinePoint[]>(`/analytics/timeline?period=${period}`),
    chats: (period: DashboardPeriod = '7d') =>
      request<AnalyticsChatBreakdownItem[]>(`/analytics/chats?period=${period}`),
  },
  deliveries: {
    getMessage: (id: string) => request<DeliveryMessageResponse>(`/deliveries/${id}/message`),
    updateMessage: (id: string, data: { text: string }) =>
      request<DeliveryMessageResponse>(`/deliveries/${id}/message`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    list: (params?: Record<string, string>) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return request<PaginatedResponse<DeliveryListItem>>(`/deliveries${query}`);
    },
    recent: (limit = 10) =>
      request<DeliveryListItem[]>(`/deliveries/recent?limit=${String(limit)}`),
  },
  messageTemplates: {
    list: (params?: Record<string, string>) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return request<PaginatedResponse<MessageTemplateResponse>>(`/message-templates${query}`);
    },
    listByChat: (chatId: string) =>
      request<MessageTemplateResponse[]>(`/chats/${chatId}/message-templates`),
    get: (id: string) => request<MessageTemplateResponse>(`/message-templates/${id}`),
    create: (data: { chatId: string; name: string; body: string }) =>
      request<MessageTemplateResponse>('/message-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { name?: string; body?: string }) =>
      request<MessageTemplateResponse>(`/message-templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/message-templates/${id}`, { method: 'DELETE' }),
  },
  integrations: {
    telegramApi: {
      getAvailability: () =>
        request<TelegramApiAvailabilityResponse>('/integrations/telegram-api/availability'),
      getStatus: () => request<TelegramApiIntegrationStatusResponse>('/integrations/telegram-api'),
      save: (data: { apiId: string; apiHash: string }) =>
        request<TelegramApiIntegrationSaveResponse>('/integrations/telegram-api', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      verify: () =>
        request<TelegramApiIntegrationStatusResponse>('/integrations/telegram-api/verify', {
          method: 'POST',
        }),
      remove: () => request<{ success: true }>('/integrations/telegram-api', { method: 'DELETE' }),
    },
  },
};
